import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Grid, List, Edit, Eye, Trash2, ChevronLeft, ChevronRight, Settings, Video } from 'lucide-react';
import { VideoData, CategoryData, timestampToString } from '@/lib/firebase';

interface VideoListProps {
  videos: VideoData[];
  categories: CategoryData[];
  onEditVideo: (video: VideoData) => void;
  onDeleteVideo: (videoId: string) => void;
}

export default function VideoList({ 
  videos, 
  categories, 
  onEditVideo, 
  onDeleteVideo
}: VideoListProps) {
  // Get initial state from URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      searchQuery: params.get('search') || '',
      categoryFilter: params.get('category') || '',
      statusFilter: params.get('status') || '',
      contributorFilter: params.get('contributor') || '',
      currentPage: parseInt(params.get('page') || '1'),
      viewMode: (params.get('view') || 'list') as 'list' | 'grid',
      itemsPerPage: parseInt(params.get('pageSize') || '10')
    };
  };

  const initialParams = getUrlParams();
  const [searchQuery, setSearchQuery] = useState(initialParams.searchQuery);
  const [categoryFilter, setCategoryFilter] = useState(initialParams.categoryFilter);
  const [statusFilter, setStatusFilter] = useState(initialParams.statusFilter);
  const [contributorFilter, setContributorFilter] = useState(initialParams.contributorFilter);
  const [currentPage, setCurrentPage] = useState(initialParams.currentPage);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(initialParams.viewMode);
  const [itemsPerPage, setItemsPerPage] = useState(initialParams.itemsPerPage);

  // Update URL parameters when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (categoryFilter) params.set('category', categoryFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (contributorFilter) params.set('contributor', contributorFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (viewMode !== 'list') params.set('view', viewMode);
    if (itemsPerPage !== 10) params.set('pageSize', itemsPerPage.toString());
    
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [searchQuery, categoryFilter, statusFilter, contributorFilter, currentPage, viewMode, itemsPerPage]);

  const getEffectiveStatus = (video: VideoData) => {
    if (video.status === 'Published' && video.publishDate) {
      // Handle both string and timestamp formats
      const publishDateString = typeof video.publishDate === 'string' ? video.publishDate : timestampToString(video.publishDate);
      const publishDate = new Date(publishDateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      publishDate.setHours(0, 0, 0, 0);
      
      if (publishDate > today) {
        return 'Scheduled';
      }
    }
    return video.status;
  };

  // Build unique contributor list from all videos
  const uniqueContributors = (() => {
    const seen = new Set<string>();
    const list: { id: string; name: string; profilePicUrl?: string }[] = [];
    videos.forEach(v => {
      const vAny = v as any;
      if (vAny.contributorIds && vAny.contributors) {
        vAny.contributorIds.forEach((id: string) => {
          if (!seen.has(id)) {
            seen.add(id);
            const contrib = vAny.contributors.find((c: any) => c.id === id);
            list.push({ id, name: contrib?.name || id, profilePicUrl: contrib?.profilePicUrl });
          }
        });
      }
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  })();

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || video.contentCategories.includes(categoryFilter);
    const effectiveStatus = getEffectiveStatus(video);
    const matchesStatus = !statusFilter || effectiveStatus === statusFilter;
    const matchesContributor = !contributorFilter
      || (contributorFilter === '__none__' ? !((video as any).contributorIds?.length > 0) : (video as any).contributorIds?.includes(contributorFilter));
    
    return matchesSearch && matchesCategory && matchesStatus && matchesContributor;
  });

  const totalPages = Math.ceil(filteredVideos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVideos = filteredVideos.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Published':
        return 'default';
      case 'Scheduled':
        return 'secondary';
      case 'Draft':
        return 'outline';
      case 'Blocked':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getCategoryNames = (categoryTags: string[] = []) => {
    if (!categoryTags.length) return 'Uncategorized';
    return categoryTags.map(tag => {
      const category = categories.find(c => c.tag === tag);
      return category?.title || tag;
    }).join(', ');
  };

  const getCategoryDisplay = (categoryTags: string[] = []) => {
    if (!categoryTags.length) return [{ name: 'Uncategorized', details: '' }];
    return categoryTags.map(tag => {
      const category = categories.find(c => c.tag === tag);
      if (category) {
        return {
          name: category.title,
          details: `${category.contentLibrary} - ${category.module}`
        };
      }
      return { name: tag, details: '' };
    });
  };

  const formatDuration = (duration: string) => {
    if (!duration) return '--:--';
    // Duration is already in format like "0:21:18.000000"
    return duration.split('.')[0]; // Remove microseconds
  };

  const formatDate = (timestamp?: any) => {
    if (!timestamp) return '--';
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString();
    }
    
    // Handle Firestore timestamps
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    
    // Handle regular Date objects
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    
    return '--';
  };

  const getModuleColors = (module: string) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return {
          bg: 'bg-[#d059c4]/10',
          border: 'border-[#d059c4]/30',
          text: 'text-[#d059c4]'
        };
      case 'mind':
        return {
          bg: 'bg-[#66cee7]/10',
          border: 'border-[#66cee7]/30',
          text: 'text-[#66cee7]'
        };
      case 'general':
        return {
          bg: 'bg-[#595ebe]/10',
          border: 'border-[#595ebe]/30',
          text: 'text-[#595ebe]'
        };
      default:
        return {
          bg: 'bg-slate-100',
          border: 'border-slate-200',
          text: 'text-slate-600'
        };
    }
  };

  return (
    <div className="space-y-8">
      {/* Filters and Search */}
      <Card className="border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Select value={categoryFilter || "all"} onValueChange={(value) => setCategoryFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category.id} value={category.tag}>
                    {category.title} ({category.contentLibrary} - {category.module})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="Published">Published</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>

            <Select value={contributorFilter || "all"} onValueChange={(value) => { setContributorFilter(value === "all" ? "" : value); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Contributors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contributors</SelectItem>
                <SelectItem value="__none__">No contributor</SelectItem>
                {uniqueContributors.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Video List */}
      <Card className="border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Video Library</h2>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {paginatedVideos.length === 0 ? (
          <div className="p-12 text-center">
            <Video className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No videos found</h3>
            <p className="text-slate-600">
              {searchQuery || categoryFilter || statusFilter
                ? 'Try adjusting your filters or search terms.'
                : 'Import your first video from Vimeo to get started.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Video</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Categories</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Duration</th>
                    <th className="text-right py-3 px-6 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {paginatedVideos.map((video) => (
                    <tr key={video.id} className="hover:bg-slate-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className={`w-16 h-10 rounded flex items-center justify-center text-white text-xs font-bold ${
                            video.mediaType === 'youtube' ? 'bg-red-600' : 'bg-blue-600'
                          }`}>
                            {video.mediaType.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{video.title}</p>
                            <p className="text-sm text-slate-500">
                              {video.channel} • {video.author}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          {getCategoryDisplay(video.contentCategories).map((cat, index) => {
                            const category = categories.find(c => c.title === cat.name);
                            const colors = category ? getModuleColors(category.module) : getModuleColors('');
                            
                            return (
                              <div key={index} className={`inline-flex flex-col px-2 py-1.5 rounded text-xs border ${colors.bg} ${colors.border} ${colors.text}`}>
                                <div className="font-medium">
                                  {cat.name}
                                </div>
                                {cat.details && (
                                  <div className="text-xs opacity-75 mt-0.5">
                                    {cat.details}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <Badge variant={getStatusBadgeVariant(getEffectiveStatus(video))}>
                            {getEffectiveStatus(video)}
                          </Badge>
                          {(getEffectiveStatus(video) === 'Published' || getEffectiveStatus(video) === 'Scheduled') && video.publishDate && (
                            <div className="text-xs text-slate-500 mt-1">
                              {formatDate(video.publishDate)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-slate-600">
                        {formatDuration(video.duration)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditVideo(video)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (video.mediaType === 'youtube') {
                                window.open(`https://www.youtube.com/watch?v=${video.externalId}`, '_blank');
                              } else if (video.mediaType === 'vimeo') {
                                // For Vimeo, use the embed URL if available, otherwise construct the regular Vimeo URL
                                const vimeoUrl = video.url?.includes('player.vimeo.com') 
                                  ? `https://vimeo.com/${video.externalId}`
                                  : video.url || `https://vimeo.com/${video.externalId}`;
                                window.open(vimeoUrl, '_blank');
                              }
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteVideo(video.id!)}
                            className="text-red-600 hover:text-red-700"
                            title="Disable video (soft delete)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-slate-600">
                  Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredVideos.length)} of {filteredVideos.length} videos
                </p>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600">Page size:</span>
                  <Select 
                    value={itemsPerPage.toString()} 
                    onValueChange={(value) => {
                      setItemsPerPage(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px]" data-testid="select-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                {(() => {
                  const maxVisible = 5;
                  let startPage = 1;
                  let endPage = Math.min(maxVisible, totalPages);
                  
                  if (currentPage > 5) {
                    startPage = currentPage - 2;
                    endPage = Math.min(currentPage + 2, totalPages);
                    
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - (maxVisible - 1));
                    }
                  }
                  
                  return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                    const page = startPage + i;
                    return (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        data-testid={`button-page-${page}`}
                      >
                        {page}
                      </Button>
                    );
                  });
                })()}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="button-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
